import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { ManageApiGet as ApiGet, ManageApiPost as ApiPost } from './Api';

const TEST_DOMAIN = 'raynertrust.co.uk';
const WORKFLOW_API_PATH = 'manage-workflow';
const WORKFLOW_STORAGE_KEY = 'manageWorkflowExecutionArn';
const REFRESH_API_PATH = 'background';

const HIGH_LEVEL_OPERATIONS = [
  {
    key: 'account-onboarding',
    title: 'Connect hosting account',
    description: 'Enable the shell and install the automation key on a new hosting account so the other operations can connect. Run this first for a new site.',
    buttonLabel: 'Connect account'
  },
  {
    key: 'php-provision',
    title: 'Prepare hosting account',
    description: 'Configure the approved PHP version and extensions before WordPress is installed.',
    buttonLabel: 'Prepare PHP'
  },
  {
    key: 'wordpress-post-install',
    title: 'Configure WordPress',
    description: 'Install the approved plugins and theme, then configure Updraft after WordPress has been installed through Installatron.',
    buttonLabel: 'Configure WordPress'
  }
];

const PHASE_LABELS = {
  starting: 'Starting',
  enabling: 'Enabling',
  enabled: 'Enabled temporarily',
  waiting: 'Enabled temporarily',
  disabling: 'Disabling',
  complete: 'Inactive',
  failed: 'Workflow failed'
};

const MANAGED_SERVICES = [
  {
    key: 'elementor-manage-all',
    title: 'Elementor Manage — all sites',
    description: 'Temporarily enable Elementor Manage on every hosted site, for a coordinated patch run through Elementor\'s own dashboard.',
    apiPath: 'plugin-window',
    scope: 'all',
    enableOperation: 'enable-window',
    disableOperation: 'disable',
    enableLabel: 'Enable for 30 minutes (all sites)',
    disableLabel: 'Disable now (all sites)',
    enableConfirmation: 'Enable Elementor Manage on every hosted site for 30 minutes? It will be disabled automatically.',
    disableConfirmation: 'Disable Elementor Manage on every hosted site now?'
  }
];

const formatDate = value => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const getErrorMessage = error => {
  if (error?.response?.data?.error) {
    return error.response.data.error;
  }

  if (error?.message) {
    return error.message;
  }

  return String(error);
};

const normaliseDomain = value => String(value ?? '').trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');

const isValidDomain = value => /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i.test(value);

const parseDomainList = value => [...new Set(
  String(value ?? '')
    .split(',')
    .map(item => normaliseDomain(item))
    .filter(Boolean)
)];

const readStoredExecutionArns = () => {
  try {
    const stored = window.localStorage.getItem(WORKFLOW_STORAGE_KEY);
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const ProvisioningOperations = () => {
  const [domain, setDomain] = useState('');
  const [pendingWorkflow, setPendingWorkflow] = useState(null);
  const [executionArns, setExecutionArns] = useState(readStoredExecutionArns);
  const [workflowStatuses, setWorkflowStatuses] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadWorkflowStatus = useCallback(async arns => {
    if (arns.length === 0) {
      return;
    }

    try {
      const statuses = await Promise.all(arns.map(arn => ApiGet(WORKFLOW_API_PATH, { executionArn: arn })));
      setWorkflowStatuses(statuses);

      const stillRunning = arns.filter((arn, index) => statuses[index]?.running === true);

      if (stillRunning.length !== arns.length) {
        if (stillRunning.length === 0) {
          window.localStorage.removeItem(WORKFLOW_STORAGE_KEY);
        } else {
          window.localStorage.setItem(WORKFLOW_STORAGE_KEY, JSON.stringify(stillRunning));
        }

        setExecutionArns(stillRunning);
      }

      setError('');
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    }
  }, []);

  useEffect(() => {
    if (executionArns.length === 0) {
      return undefined;
    }

    loadWorkflowStatus(executionArns);
    const timer = window.setInterval(() => loadWorkflowStatus(executionArns), 5000);
    return () => window.clearInterval(timer);
  }, [executionArns, loadWorkflowStatus]);

  const startWorkflow = async operation => {
    const targetDomains = parseDomainList(domain);

    if (targetDomains.length === 0) {
      setMessage('');
      setError('Enter a valid domain name, or a comma-separated list of domain names, for example example.co.uk.');
      return;
    }

    const invalidDomains = targetDomains.filter(value => !isValidDomain(value));

    if (invalidDomains.length > 0) {
      setMessage('');
      setError(`Enter valid domain names, for example example.co.uk. Not valid: ${invalidDomains.join(', ')}`);
      return;
    }

    const confirmationTarget = targetDomains.length === 1
      ? targetDomains[0]
      : `${targetDomains.length} sites (${targetDomains.join(', ')})`;

    if (!window.confirm(`${operation.title} for ${confirmationTarget}?`)) {
      return;
    }

    setPendingWorkflow(operation.key);
    setMessage('');
    setError('');

    try {
      const result = await ApiPost(WORKFLOW_API_PATH, {
        domain: targetDomains,
        workflow: operation.key
      });

      const results = Array.isArray(result?.results) ? result.results : [];
      const startedArns = results.filter(item => item.executionArn).map(item => item.executionArn);

      if (startedArns.length === 0) {
        throw new Error('The server did not start any workflow executions');
      }

      window.localStorage.setItem(WORKFLOW_STORAGE_KEY, JSON.stringify(startedArns));
      setExecutionArns(startedArns);
      setWorkflowStatuses(results.map(item => ({ ...item, status: item.started ? 'RUNNING' : 'FAILED_TO_START' })));

      const failedToStart = results.filter(item => !item.started);

      if (failedToStart.length > 0) {
        setError(`Did not start for: ${failedToStart.map(item => `${item.domain} (${item.error})`).join('; ')}`);
      } else {
        setMessage('');
      }
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setPendingWorkflow(null);
    }
  };

  return (
    <>
      <Row className="mt-4">
        <Col>
          <h2 className="text-center">Site provisioning</h2>
          <p className="text-center">Enter one or more site domains (comma-separated), then choose the operation to run.</p>
        </Col>
      </Row>

      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <Form.Group controlId="manage-domain">
            <Form.Label>Domain(s)</Form.Label>
            <Form.Control
              type="text"
              value={domain}
              disabled={pendingWorkflow !== null}
              placeholder="domain name, or a comma-separated list of domain names"
              autoComplete="off"
              onChange={event => setDomain(event.target.value)}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                }
              }}
            />
          </Form.Group>

          {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
          {message && <Alert variant="info" className="mt-3">{message}</Alert>}

          {workflowStatuses.map(status => (
            <Alert
              key={status.executionArn ?? status.domain}
              variant={
                status.status === 'RUNNING'
                  ? 'info'
                  : status.status === 'SUCCEEDED'
                    ? 'success'
                    : 'danger'
              }
              className="mt-3"
            >
              {status.status === 'RUNNING' && (
                <>
                  <strong>Workflow running.</strong>
                  <div>{status.workflow} is running for {status.domain}.</div>
                  {status.startedAt && <div>Started: {formatDate(status.startedAt)}</div>}
                </>
              )}

              {status.status === 'SUCCEEDED' && (
                <>
                  <strong>Workflow completed successfully.</strong>
                  <div>{status.workflow} completed for {status.domain}.</div>
                  {status.stoppedAt && <div>Finished: {formatDate(status.stoppedAt)}</div>}
                </>
              )}

              {!['RUNNING', 'SUCCEEDED'].includes(status.status) && (
                <>
                  <strong>Workflow failed.</strong>
                  <div>{status.workflow} did not complete for {status.domain}.</div>
                  {status.error && <div>Error: {status.error}</div>}
                  {status.cause && <div>{status.cause}</div>}
                  {status.stoppedAt && <div>Stopped: {formatDate(status.stoppedAt)}</div>}
                </>
              )}
            </Alert>
          ))}
        </Col>
      </Row>

      <Row className="justify-content-center">
        {HIGH_LEVEL_OPERATIONS.map(operation => (
          <Col md={6} lg={5} className="mt-3" key={operation.key}>
            <div className="border rounded p-3 h-100 text-center">
              <h3>{operation.title}</h3>
              <p>{operation.description}</p>
              <Button variant="primary" disabled={pendingWorkflow !== null || executionArns.length > 0} onClick={() => startWorkflow(operation)}>
                {pendingWorkflow === operation.key ? <><Spinner animation="border" size="sm" /> Starting…</> : operation.buttonLabel}
              </Button>
            </div>
          </Col>
        ))}
      </Row>
    </>
  );
};

const ManagedService = ({ service }) => {
  const [loading, setLoading] = useState(true);
  const [pendingOperation, setPendingOperation] = useState(null);
  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState(null);
  const [startedAt, setStartedAt] = useState(null);
  const [disableAt, setDisableAt] = useState(null);
  const [domain, setDomain] = useState(service.scope === 'single' ? TEST_DOMAIN : null);
  const [error, setError] = useState('');

  const loadStatus = useCallback(async () => {
    try {
      const result = await ApiGet(service.apiPath);
      setRunning(result?.running === true);
      setPhase(result?.phase ?? null);
      setStartedAt(result?.startedAt ?? null);
      setDisableAt(result?.disableAt ?? null);
      setDomain(result?.running ? (result?.domain ?? null) : (service.scope === 'single' ? TEST_DOMAIN : null));
      setError('');
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, [service.apiPath, service.scope]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (!running) {
      return undefined;
    }

    const interval = phase !== 'waiting' ? 5000 : 30000;
    const timer = window.setInterval(loadStatus, interval);
    return () => window.clearInterval(timer);
  }, [running, phase, loadStatus]);

  const startOperation = async operation => {
    setPendingOperation(operation);
    setError('');

    try {
      const result = await ApiPost(service.apiPath, { operation, scope: service.scope });
      setRunning(true);
      setPhase(operation === service.enableOperation ? 'starting' : 'disabling');
      setStartedAt(result?.startedAt ?? new Date().toISOString());
      setDisableAt(result?.disableAt ?? null);
      setDomain(result?.domain ?? (service.scope === 'single' ? TEST_DOMAIN : null));
    } catch (requestError) {
      setError(getErrorMessage(requestError));
      await loadStatus();
    } finally {
      setPendingOperation(null);
    }
  };

  const disableBlocked = loading || pendingOperation !== null || (running && ['starting', 'enabling', 'disabling'].includes(phase));

  const renderStatus = () => {
    if (loading) {
      return <div className="text-center"><Spinner animation="border" /></div>;
    }

    const targetLabel = domain ?? 'every hosted site';

    if (!running) {
      return <Alert variant="secondary"><strong>{service.title} is inactive.</strong><div>No temporary-access workflow is currently running.</div><div>Target: {targetLabel}</div></Alert>;
    }

    return (
      <Alert variant={phase === 'failed' ? 'danger' : 'warning'}>
        <div><strong>{service.title}: {PHASE_LABELS[phase] ?? 'Workflow running'}</strong></div>
        <div>Target: {targetLabel}</div>
        {startedAt && <div>Started: {formatDate(startedAt)}</div>}
        {disableAt && <div>Automatic disable: {formatDate(disableAt)}</div>}
        {!disableAt && phase !== 'disabling' && <div>Automatic disable is scheduled by the workflow.</div>}
      </Alert>
    );
  };

  return (
    <>
      <Row className="mt-5">
        <Col><h2 className="text-center">{service.title}</h2><p className="text-center">{service.description}</p></Col>
      </Row>
      <Row><Col>{error && <Alert variant="danger"><strong>Unable to complete the request.</strong><div>{error}</div></Alert>}{renderStatus()}</Col></Row>
      <Row>
        <Col>
          <div className="text-center">
            <Button variant="primary" disabled={loading || pendingOperation !== null || running} onClick={() => window.confirm(service.enableConfirmation) && startOperation(service.enableOperation)}>
              {pendingOperation === service.enableOperation ? 'Enabling…' : service.enableLabel}
            </Button>{' '}
            <Button variant="danger" disabled={disableBlocked} onClick={() => window.confirm(service.disableConfirmation) && startOperation(service.disableOperation)}>
              {pendingOperation === service.disableOperation ? 'Disabling…' : service.disableLabel}
            </Button>{' '}
            <Button variant="outline-secondary" disabled={loading || pendingOperation !== null} onClick={loadStatus}>Refresh status</Button>
          </div>
        </Col>
      </Row>
    </>
  );
};

const RefreshData = () => {
  const [loading, setLoading] = useState(true);
  const [pendingStart, setPendingStart] = useState(false);
  const [running, setRunning] = useState(false);
  const [status, setStatus] = useState(null);
  const [startedAt, setStartedAt] = useState(null);
  const [stoppedAt, setStoppedAt] = useState(null);
  const [error, setError] = useState('');

  const loadStatus = useCallback(async () => {
    try {
      const result = await ApiGet(REFRESH_API_PATH);
      setRunning(result?.running === true);
      setStatus(result?.status ?? null);
      setStartedAt(result?.startedAt ?? null);
      setStoppedAt(result?.stoppedAt ?? null);
      setError('');
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (!running) {
      return undefined;
    }

    const timer = window.setInterval(loadStatus, 5000);
    return () => window.clearInterval(timer);
  }, [running, loadStatus]);

  const startRefresh = async () => {
    if (!window.confirm('Refresh cached hosting, uptime, inventory, registration, backup and email data now?')) {
      return;
    }

    setPendingStart(true);
    setError('');

    try {
      const result = await ApiPost(REFRESH_API_PATH);
      setRunning(true);
      setStatus('RUNNING');
      setStartedAt(result?.startedAt ?? new Date().toISOString());
      setStoppedAt(null);
    } catch (requestError) {
      setError(getErrorMessage(requestError));
      await loadStatus();
    } finally {
      setPendingStart(false);
    }
  };

  const renderStatus = () => {
    if (loading) {
      return <div className="text-center"><Spinner animation="border" /></div>;
    }

    if (running) {
      return (
        <Alert variant="info">
          <strong>Refresh in progress.</strong>
          {startedAt && <div>Started: {formatDate(startedAt)}</div>}
        </Alert>
      );
    }

    if (status === 'SUCCEEDED') {
      return (
        <Alert variant="success">
          <strong>Data is up to date.</strong>
          {stoppedAt && <div>Last refreshed: {formatDate(stoppedAt)}</div>}
        </Alert>
      );
    }

    if (status) {
      return (
        <Alert variant="danger">
          <strong>Last refresh did not complete successfully.</strong>
          <div>Status: {status}</div>
          {stoppedAt && <div>Stopped: {formatDate(stoppedAt)}</div>}
        </Alert>
      );
    }

    return <Alert variant="secondary">No refresh has been run yet.</Alert>;
  };

  return (
    <>
      <Row className="mt-5">
        <Col>
          <h2 className="text-center">Refresh Data</h2>
          <p className="text-center">Re-run the background collector to update hosting, uptime, inventory, registration, backup and email data now, instead of waiting for the daily schedule.</p>
        </Col>
      </Row>
      <Row><Col>{error && <Alert variant="danger"><strong>Unable to complete the request.</strong><div>{error}</div></Alert>}{renderStatus()}</Col></Row>
      <Row>
        <Col>
          <div className="text-center">
            <Button variant="primary" disabled={loading || pendingStart || running} onClick={startRefresh}>
              {pendingStart ? <><Spinner animation="border" size="sm" /> Starting…</> : 'Refresh Data'}
            </Button>
          </div>
        </Col>
      </Row>
    </>
  );
};

const Manage = () => (
  <Container fluid="lg">
    <Row><Col><h1 className="text-center">Manage</h1></Col></Row>
    <RefreshData />
    <ProvisioningOperations />
    {MANAGED_SERVICES.map(service => <ManagedService key={service.key} service={service} />)}
  </Container>
);

export { Manage };
